import { createColumnHelper, type Row } from '@tanstack/react-table';

import { OrderCurrency } from '@invenco/sales-interface/interfaces';

import { formatMoney, formatNumber } from '@/shared/helpers';

import { getOrderLineTotal, isBundleComponentLine, type OrderLineRow } from './order-line-row';
import { SkuDescriptionCell } from './sku-description-cell';

const columnHelper = createColumnHelper<OrderLineRow>();

type GetOrderLineColumnsOptions = {
  orderCurrency?: OrderCurrency;
  reserveTypeIconSpace?: boolean;
};

function formatOptionalMoney(value: number | null | undefined, currency = OrderCurrency.AUD) {
  if (value === null || value === undefined) return undefined;
  return formatMoney(value, currency);
}

function formatOptionalNumber(value: number | null | undefined, precision = 0) {
  if (value === null || value === undefined) return undefined;
  return formatNumber(value, precision);
}

function formatDiscountValue(value: string | null | undefined, currency = OrderCurrency.AUD) {
  const discount = value ?? '0%';
  if (discount.endsWith('%')) {
    return `${formatNumber(Number(discount.slice(0, -1)) || 0, 2)}%`;
  }

  return formatMoney(Number(discount) || 0, currency);
}

function BlankOrderLineCell() {
  return <span className="invisible" aria-hidden />;
}

function isLastSubRow(row: Row<OrderLineRow>) {
  const subRows = row.getParentRow()?.subRows;
  return subRows?.[subRows.length - 1]?.id === row.id;
}

export function getOrderLineColumns({
  orderCurrency = OrderCurrency.AUD,
  reserveTypeIconSpace,
}: GetOrderLineColumnsOptions = {}) {
  return [
    columnHelper.accessor('description', {
      header: 'SKU / description',
      cell: ({ row }) => (
        <SkuDescriptionCell
          line={row.original}
          isLastComponent={isLastSubRow(row)}
          reserveTypeIconSpace={reserveTypeIconSpace}
        />
      ),
      meta: {
        truncate: true,
        cellClassName: (cell) =>
          isBundleComponentLine(cell.row.original) ? 'relative' : undefined,
        editable: {
          label: 'Description',
          editor: {
            type: 'text',
          },
        },
      },
    }),
    columnHelper.accessor('qtyOrdered', {
      header: 'Qty',
      cell: ({ getValue, row }) =>
        isBundleComponentLine(row.original) ? (
          <BlankOrderLineCell />
        ) : (
          formatOptionalNumber(getValue(), 0)
        ),
      meta: {
        align: 'right',
        editable: {
          label: 'Qty',
          editor: {
            type: 'number',
            min: 0,
            step: 1,
          },
        },
      },
    }),
    columnHelper.accessor('unitPrice', {
      header: 'Unit cost',
      cell: ({ getValue, row }) =>
        isBundleComponentLine(row.original) ? (
          <BlankOrderLineCell />
        ) : (
          formatOptionalMoney(getValue(), orderCurrency)
        ),
      meta: {
        align: 'right',
        editable: {
          label: 'Unit cost',
          editor: {
            type: 'number',
            min: 0,
            step: 'any',
          },
        },
      },
    }),
    columnHelper.accessor('discount', {
      header: 'Discount',
      cell: ({ getValue, row }) =>
        isBundleComponentLine(row.original) ? (
          <BlankOrderLineCell />
        ) : (
          formatDiscountValue(getValue(), orderCurrency)
        ),
      meta: {
        align: 'right',
        editable: {
          label: 'Discount',
          editor: {
            type: 'text',
          },
        },
      },
    }),
    columnHelper.accessor('lineTotal', {
      header: 'Line total',
      cell: ({ row }) =>
        isBundleComponentLine(row.original) ? (
          <BlankOrderLineCell />
        ) : (
          formatMoney(getOrderLineTotal(row.original), orderCurrency)
        ),
      meta: {
        align: 'right',
        editable: {
          renderFromDraft: (draft) => formatMoney(getOrderLineTotal(draft), orderCurrency),
        },
      },
    }),
  ];
}
